import { Injectable, RendererFactory2, Renderer2, inject } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { environment } from '@environments/environment';
import { select } from '@angular-redux/store';
import { User } from '@core/redux/stores';
import { AuthTrackingService } from './authTracking.service';
import _ from 'lodash';
import { AccessibilityService } from './accessibility.service';

export interface ChatUserContext {
  td_contact_person_identification: string;
  field_name1: string;
  field_option1: string;
  field_option2: string;
  field_email1: string;
  'contact.address': string;
  Mobile_App_Id: string;
  Customer_Phone_Number: string;
  field_option3: string;
}

@Injectable({ providedIn: 'root' })
export class ChatService {
  private webchat: any;
  private subscriptions = new Subscription();
  public startChat = false;
  private hideRoutes: string[] = ['order-review', 'contact-us', 'locations','menu','ordering','customization'];

  @select((appState) => appState.user)
  userObject: Observable<User>;
  private pendingContext: ChatUserContext;
  private user: User;
  private renderer = inject(RendererFactory2).createRenderer(null, null);
  private authTrackingService = inject(AuthTrackingService);
  private accessibilityService = inject(AccessibilityService);
  lastAccessibilityWidgetPosition: string;
  constructor() {
    this.accessibilityService.isChatOpen = false;
    this.subscribeToUser();
  }

  private subscribeToUser(): void {
    this.subscriptions.add(
      this.userObject.subscribe((user) => {
      this.user = _.cloneDeep(user);
      if (!user) return;

      const context = (user?.id ?? false) ? this.getUserContext(user) : null;
      this.pendingContext = context;

      if (this.webchat) {
        this.setContext(context);
      }
      })
    );
  }

  reset() {
    if(this.webchat) {
      this.webchat.reset();
    }
  }

  private getUserContext(user: User) {
    const fullName = `${user.firstName} ${user.lastName}`;
    return {
      td_contact_person_identification: user.email,
      field_name1: fullName,
      field_option1: user.firstName,
      field_option2: user.lastName,
      field_email1: user.email,
      'contact.address': user.email,
      Mobile_App_Id: user.beanstalkContactId,
      Customer_Phone_Number: user.phoneNumber,
      field_option3: user.phoneNumber
    };
  }

  loadSDK(): void {
    // If SDK is already loaded but webchat instance doesn't exist, initialize it
    if (window['TalkdeskChatSDK'] && !this.webchat) {
      console.log('SDK already loaded, initializing webchat instance');
      this.injectSdkElements();
      this.initSdk();
      return;
    }
    
    // If webchat instance already exists, we're done
    if (this.webchat) {
      console.warn('Webchat instance already exists');
      return;
    }

    // Check if we're in Safari and log
    const isSafari = this.isSafari();

    this.injectSdkElements();

    // In Safari, add a small delay before loading the script to prevent connection pooling issues
    const delay = isSafari ? 300 : 0;
    
    setTimeout(() => {
      const script = this.renderer.createElement('script');
      script.type = 'text/javascript';
      script.charset = 'UTF-8';
      script.id = 'tdwebchatscript';
      script.src = 'https://talkdeskchatsdk.talkdeskapp.com/v2/talkdeskchatsdk.js';
      script.async = true;

      script.onload = () => {
        console.log('TalkDesk SDK script loaded successfully');
        this.initSdk();
      };

      script.onerror = (error) => {
        console.error('Error loading TalkDesk SDK script:', error);
      };

      const firstScriptTag = document.getElementsByTagName('script')[0];
      this.renderer.insertBefore(firstScriptTag.parentNode, script, firstScriptTag);
    }, delay);
  }

  private isSafari() {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isSafari) {
      console.log('Safari detected - loading TalkDesk SDK');
    }
    return isSafari;
  }

  private injectSdkElements(): void {
    // Check if container already exists
    let divContainer = document.getElementById('tdWebchat');
    
    if (!divContainer) {
      divContainer = this.renderer.createElement('div');
      divContainer.id = 'tdWebchat';
      this.renderer.appendChild(document.body, divContainer);
      console.log('TalkDesk container created');
    } else {
      console.log('TalkDesk container already exists');
    }
  }

  private initSdk(): void {
    console.log('Initializing TalkDesk SDK...', {
      touchpointId: environment.TalkDesk.TouchpointId,
      region: environment.TalkDesk.Region
    });
    
    this.webchat = window['TalkdeskChatSDK']('tdWebchat', {
      touchpointId: environment.TalkDesk.TouchpointId,
      accountId: '',
      region: environment.TalkDesk.Region
    });

    this.webchat.init({
      enableValidation: false,
      enableEmoji: true,
      enableUserInput: true,
      enableAttachments: true,
      enablePointMoveTriggerButton: true,
      styles: {}
    }).then(() => {
      console.log('TalkdeskChatSDK initialized successfully');
        if (this.pendingContext) {
          this.setContext(this.pendingContext);
      }
      setTimeout(() => {
        if (this.startChat) {
          this.hideExternalWidgets();
          this.webchat.open();
        }
        
        // Log chat bubble visibility after initialization
        const chatBubble = document.getElementById('talkdesk-chat-widget-trigger');
        if (chatBubble) {
          console.log('Chat bubble found:', chatBubble.style.display);
        } else {
          console.warn('Chat bubble not found in DOM');
        }
      }, 200);
    }).catch((error) => {
      console.error('TalkdeskChatSDK initialization error:', error);
    });

    this.webchat.onConversationStart = this.onConversationStart.bind(this);
    this.webchat.onOpenWebchat = () => { this.accessibilityService.isChatOpen = true; this.hideExternalWidgets() };
    this.webchat.onConversationEnded = () => { console.log('Chat Ended') };
    this.webchat.onCloseWebchat = () => { this.accessibilityService.isChatOpen = false; this.showExternalWidgets() };
  }

  private onConversationStart(): void {
    let customerFirstName = '';
    let customerLastName = '';
    let customerEmail = '';
    let customerName = '';
    let customerPhone = '';

    if (this.authTrackingService?.isLoggedIn() && this.user?.id) {
      customerFirstName = this.user.firstName ?? '';
      customerLastName = this.user.lastName ?? '';
      customerEmail = this.user.email ?? '';
      customerName = `${customerFirstName} ${customerLastName}`; 
      customerPhone = this.user.phoneNumber ?? '';          
    }

    if(customerEmail && customerName) {
      this.setContext({
        td_contact_person_identification: customerEmail,
        field_name1: customerName,
        field_email1: customerEmail,
        'contact.address': customerEmail,
        field_option1: customerFirstName,
        field_option2: customerLastName,
        field_option3: customerPhone,
        Customer_Phone_Number: customerPhone,
        Mobile_App_Id: this.user.beanstalkContactId ?? ''
      });
    }
  }

  public hideExternalWidgets(): void {
    this.lastAccessibilityWidgetPosition = this.accessibilityService.setAccessibilityWidgetCSSText(this.accessibilityService.DISPLAYNONEIMPORTANT, true);
    if(this.startChat) {
      const feedbackButton = document.getElementById('QSIFeedbackButton-btn');
      if (feedbackButton) {
        feedbackButton.style.visibility = 'hidden';
      }
    }
  }
  public showExternalWidgets(): void {
    this.accessibilityService.setAccessibilityWidgetCSSText(this.lastAccessibilityWidgetPosition ?? this.accessibilityService.DISPLAYINLINEBLOCKIMPORTANT);
    if(this.startChat) {
      const feedbackButton = document.getElementById('QSIFeedbackButton-btn');
      if (feedbackButton) {
        feedbackButton.style.visibility = 'visible';
      }
    }
  }

  public setContext(context: ChatUserContext): void {
    if (this.webchat) {
      this.webchat.setContextParam(context);
    }
  }

  public launchChat(): void {
    if (this.webchat) {
      this.webchat.open();
    }
  }

  public hideChatWidget(): void {
    if (this.webchat) {
      this.webchat.close();
    }
  }

  public destroyChatWidget(): void {
    if (this.webchat) {
      this.webchat.destroy();
    }
  }

  public hideChatBubble(): void {
    const chatBubble = document.getElementById('talkdesk-chat-widget-trigger');
    if (chatBubble) {
      chatBubble.style.display = 'none';
    }
  }

  public showChatBubble(): void {
    const chatBubble = document.getElementById('talkdesk-chat-widget-trigger');
    if (chatBubble) {
      chatBubble.style.display = 'block';
    }
  }

  public setHideRoutes(routes: string[]): void {
    this.hideRoutes = routes;
    this.checkRouteAndHideChat();
  }

  public checkRouteAndHideChat(): void {
    const currentPath = window.location.pathname;
    
    const shouldHideByRoute = this.hideRoutes.some(route => 
      currentPath === route || currentPath.includes(route)
    );
    
    const chatIsActive = this.isChatActive();
    
    if (shouldHideByRoute && !chatIsActive) {
      this.hideChatBubble();
    } else if (!shouldHideByRoute) {
      this.showChatBubble();
    }
  }

  private isChatActive(): boolean {
    return this.accessibilityService.isChatOpen || this.hasActiveConversation();
  }

  private hasActiveConversation(): boolean {
    // Check if webchat exists and has active conversation
    if (this.webchat && this.webchat.getConversationState) {
      return this.webchat.getConversationState() === 'active';
    }
    
    // Fallback: check if conversation has started
    return this.startChat;
  }
  
}